import React, { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import Portal from '../../atoms/Portal/Portal';
import Btn, { BtnModifier } from '../../molecules/Btn';

// enums
import { Icons } from '../../atoms/SVG';
import { TooltipPlacement } from '../../atoms/Tooltip';

import { Namespace } from '../../../translation/namespace';
import { ModalMutationObserver } from './ModalMutationObserver';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import bem from '../../../lib/bemUtils/bem';
import { getFocusableElements } from '../../../lib/DomUtils/utils';
import ModalContext from './ModalContext';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { Modifier } from '../../../lib/bemUtils/Modifier';

export enum ModalDialogModifier {
  WITH_NAVIGATION = 'with-navigation',
  POP_UP = 'pop-up',
  SLIDER_IN_POP_UP = 'slider-in-pop-up',
  WIDE = 'wide',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export const ModalDialogBodyNoScrolling = 'modal-dialog__wrapper--body-no-scrolling';

type ModalDialogProps = {
  closeModal(): void;
  headline: ReactNode;
  initialFocusIndex?: number;
  focusOnReturn?: RefObject<HTMLElement | HTMLButtonElement | undefined | null>;
  modifier?: Modifier<ModalDialogModifier>;
  children: ReactNode | ((ref: RefObject<HTMLElement | null>) => ReactNode);
};

export default function ModalDialog({
  closeModal,
  headline,
  children,
  modifier = undefined,
  initialFocusIndex = undefined,
  focusOnReturn,
}: ModalDialogProps) {
  const modalRef = useRef<HTMLDialogElement>(null);
  const focusRef = useRef<HTMLElement>(null);
  const mutationObserverRef = useRef<ModalMutationObserver | null>(null);
  const { t } = useTranslation(Namespace.ModalDialog);

  const { resetAccessKeys, disableAccessKeys } = useAppContext();
  const [preventClose, setPreventClose] = useState(false);
  const [closePreventedMessage, setClosePreventedMessage] = useState<string | null>(null);

  const idModalHeadline = 'modal-dialog-label';

  const getAppWrapper = () => {
    return document.getElementById(GlobalDocumentIds.genericPageTemplate);
  };

  /**
   * - Hiding the main document from screen readers via aria-hidden="true"
   * - Setting inert on the app wrapper so it's excluded from accessibility and tab order
   */
  const hideMainContent = () => {
    const appWrapper = getAppWrapper();
    if (appWrapper) {
      appWrapper.setAttribute('aria-hidden', 'true');
      appWrapper.setAttribute('inert', '');
    }
  };

  const showMainContent = () => {
    const appWrapper = getAppWrapper();
    if (appWrapper) {
      appWrapper.removeAttribute('aria-hidden');
      appWrapper.removeAttribute('inert');
    }
  };

  const attemptClose = useCallback(
    (
      event?:
        React.MouseEvent<HTMLButtonElement | HTMLAnchorElement> | React.SyntheticEvent<HTMLDialogElement> | undefined
    ) => {
      if (preventClose) {
        event?.preventDefault();
        setClosePreventedMessage(t(`closingModalDialogBlocked`));
      } else {
        // Reset the app wrapper and notify the parent so the main content is interactive
        // again and the dialog unmounts.
        showMainContent();
        sendLiveRegionMessage(t(`modalDialogWasClosed`));
        // Fire the native close() so the browser restores focus to the element that was
        // focused before showModal(), before the dialog unmounts.
        modalRef.current?.close();
        // If that element is no longer available (e.g. a delete form removed it), the native
        // focus restoration falls back to the document body. focusOnReturn lets the caller
        // redirect focus explicitly in that case.
        if (
          focusOnReturn?.current &&
          document.contains(focusOnReturn.current) &&
          document.activeElement === document.body
        ) {
          focusOnReturn.current.focus();
        }
        closeModal();
      }
    },
    [preventClose, t, closeModal]
  );

  useEffect(() => {
    hideMainContent();
    disableAccessKeys();
    modalRef.current?.showModal();

    if (modalRef.current) {
      mutationObserverRef.current = new ModalMutationObserver(modalRef.current);
    }

    let focusableElement: HTMLElement | undefined;

    if (focusRef.current) {
      focusableElement = focusRef.current;
    } else if (initialFocusIndex !== undefined && modalRef.current) {
      const focusableElements = getFocusableElements(modalRef.current);
      const targetElement = focusableElements[initialFocusIndex];
      if (targetElement) {
        focusableElement = targetElement;
      }
    } else if (modalRef.current) {
      const focusableElements = getFocusableElements(modalRef.current);
      if (focusableElements.length > 0) {
        focusableElement = focusableElements[0];
      }
    }

    if (focusableElement) {
      focusableElement.focus();
      if (focusableElement instanceof HTMLInputElement || focusableElement instanceof HTMLTextAreaElement) {
        focusableElement.select();
      }
    }

    return () => {
      mutationObserverRef.current?.disconnect();
      mutationObserverRef.current = null;
      showMainContent();
      resetAccessKeys();
    };
  }, []);

  const modalContextValue = useMemo(
    () => ({
      preventModalClose: setPreventClose,
      closePreventedMessage,
      closeModal: attemptClose,
    }),
    [setPreventClose, closePreventedMessage, attemptClose]
  );

  const isPopUp = () => {
    if (Array.isArray(modifier) && modifier.length > 0) {
      if (modifier.includes(ModalDialogModifier.POP_UP)) return true;
    }
    return modifier === ModalDialogModifier.POP_UP;
  };

  return (
    <Portal>
      <dialog
        className={bem('modal-dialog', modifier)}
        aria-modal="true"
        aria-labelledby={idModalHeadline}
        id="modal-dialog"
        ref={modalRef}
        onCancel={attemptClose}
      >
        <div className="modal-dialog__document" role="document">
          <div className="modal-dialog__header">
            <h2 className="modal-dialog__headline" id={idModalHeadline}>
              {headline}
            </h2>
            <div className="modal-dialog__close-top">
              <Btn
                text={t(`${Namespace.phrases}:close`)}
                titleText={t(`${Namespace.phrases}:close`)}
                hiddenText
                modifier={isPopUp() ? undefined : [BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
                svgSpriteId={isPopUp() ? Icons.SHRINK_ARROWS : Icons.DECLINE_CROSS}
                tooltipPlacement={isPopUp() ? TooltipPlacement.RIGHT : TooltipPlacement.TOP}
                ariaDisabled={preventClose}
                onClick={attemptClose}
                aria-controls="modal-dialog"
              />
            </div>
          </div>
          <div className="modal-dialog__body">
            <ModalContext.Provider value={modalContextValue}>
              {typeof children === 'function' ? children(focusRef) : children}
            </ModalContext.Provider>
          </div>
          <div className="modal-dialog__footer"></div>
        </div>
      </dialog>
    </Portal>
  );
}
